import { useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Label } from '../../../components/ui/label';
import { toast } from 'sonner';
import { Download, FileSpreadsheet, FileText, Calendar } from 'lucide-react';

const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
  { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
  { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
];

export default function FinanceExports() {
  const { api } = useAuth();
  const [exporting, setExporting] = useState(false);
  
  // Export options - use "all" instead of empty string for Select compatibility
  const [exportYear, setExportYear] = useState(new Date().getFullYear());
  const [exportMonth, setExportMonth] = useState('all');
  const [exportType, setExportType] = useState('transactions');

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  const handleExport = async () => {
    setExporting(true);
    try {
      let endpoint = '';
      let filename = '';
      
      if (exportType === 'transactions') {
        endpoint = `/finance/export/transactions?year=${exportYear}`;
        filename = `transactions_${exportYear}`;
        if (exportMonth && exportMonth !== 'all') {
          endpoint += `&month=${exportMonth}`;
          filename += `_${exportMonth.padStart(2, '0')}`;
        }
        filename += '.csv';
      } else if (exportType === 'monthly-report') {
        if (!exportMonth || exportMonth === 'all') {
          toast.error('Please select a month for the monthly report');
          setExporting(false);
          return;
        }
        endpoint = `/finance/export/monthly-report?year=${exportYear}&month=${exportMonth}`;
        filename = `monthly_report_${exportYear}_${exportMonth.padStart(2, '0')}.csv`;
      }
      
      const response = await api.get(endpoint, {
        responseType: 'blob'
      });
      
      // Download the file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export downloaded successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to generate export');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="finance-exports">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Transaction Export */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              Transaction Export
            </CardTitle>
            <CardDescription>
              Export all transactions for a specific period as CSV for your accountant.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Year</Label>
              <Select value={exportYear.toString()} onValueChange={(v) => setExportYear(parseInt(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map(y => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Month (optional)</Label>
              <Select value={exportMonth} onValueChange={setExportMonth}>
                <SelectTrigger>
                  <SelectValue placeholder="All months" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All months</SelectItem>
                  {MONTHS.map(m => (
                    <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">Leave empty to export entire year</p>
            </div>
            
            <Button 
              onClick={() => { setExportType('transactions'); handleExport(); }}
              disabled={exporting}
              className="w-full"
            >
              <Download className="h-4 w-4 mr-2" />
              {exporting ? 'Exporting...' : 'Export Transactions CSV'}
            </Button>
          </CardContent>
        </Card>

        {/* Monthly Report Export */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Monthly Report Export
            </CardTitle>
            <CardDescription>
              Export a monthly summary report with category breakdowns.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Year</Label>
              <Select value={exportYear.toString()} onValueChange={(v) => setExportYear(parseInt(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map(y => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Month *</Label>
              <Select value={exportMonth} onValueChange={setExportMonth}>
                <SelectTrigger>
                  <SelectValue placeholder="Select month" />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map(m => (
                    <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <Button 
              onClick={() => { setExportType('monthly-report'); handleExport(); }}
              disabled={exporting || !exportMonth || exportMonth === 'all'}
              className="w-full"
            >
              <Download className="h-4 w-4 mr-2" />
              {exporting ? 'Exporting...' : 'Export Monthly Report CSV'}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Export Information */}
      <Card className="bg-slate-50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <Calendar className="h-6 w-6 text-slate-400 mt-1" />
            <div>
              <h4 className="font-medium text-slate-900">Export Tips for Your Accountant</h4>
              <ul className="text-sm text-slate-600 mt-2 space-y-1">
                <li>• Transaction exports include: Date, Vendor, Description, Amount, Type, Category, Payment Method</li>
                <li>• Monthly reports include: Summary totals and expense breakdown by category</li>
                <li>• All amounts in CAD (Canadian Dollars)</li>
                <li>• CSV format opens in Excel, Google Sheets, or any spreadsheet software</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
